<template>
  <div class="p-3 pm-tab-shell h-100">
    <!-- Toolbar -->
    <div class="pm-tab-toolbar d-flex justify-content-end mb-3">
      <button class="btn btn-sm btn-outline-secondary mb-0" style="font-size:0.8rem;" @click="openCreate">
        <i class="fas fa-plus me-1"></i> Create Project
      </button>
    </div>

    <!-- Table -->
    <div class="pm-tab-table-wrap">
      <div v-if="loading" class="text-center py-5 d-flex align-items-center justify-content-center flex-grow-1">
        <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
        <p class="text-secondary text-sm mt-2 mb-0">Loading...</p>
      </div>
      <div v-else class="pm-table pm-tab-table">
        <div class="pm-table-header pm-tab-table-header">
          <span class="col-name">PROJECT NAME</span>
          <span class="col-total text-center">TOTAL SUB PROJECTS</span>
          <span class="col-action text-center">ACTION</span>
        </div>
        <div class="pm-tab-table-body">
        <div v-if="projects.length === 0" class="pm-empty-table-row">
          <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
          <p class="text-secondary text-sm mt-2 mb-0">No projects yet. Click "Create Project" to add one.</p>
        </div>
        <div v-for="p in projects" :key="p.projectId" class="pm-row">
          <div class="col-name">
            <button
              class="pm-name-btn mb-0"
              @click="openDetail(p)"
            >{{ p.projectName }}</button>
          </div>
          <div class="col-total text-center">
            <span class="text-sm text-secondary">{{ p.totalSub || 0 }}</span>
          </div>
          <div class="col-action text-center">
            <button class="btn btn-link btn-sm p-0 action-edit me-2" title="Edit" @click="openEdit(p)">
              <i class="fas fa-edit" style="font-size:0.85rem;"></i>
            </button>
            <button class="btn btn-link btn-sm p-0 action-delete" title="Delete" @click="remove(p)">
              <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Modal: Create / Edit Project -->
    <Teleport to="body">
      <div v-if="showEditModal" class="modal-overlay">
        <div class="modal-box" style="width:440px;">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">{{ editId ? 'Edit Project' : 'Create Project' }}</h6>
          </div>
          <div class="p-3">
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Project Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control form-control-sm" v-model="form.name" placeholder="Enter project name" />
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 p-3 border-top">
            <div v-if="saveError" class="me-auto text-danger text-xs align-self-center">{{ saveError }}</div>
            <button class="btn btn-sm btn-outline-secondary mb-0" @click="closeEditModal">Cancel</button>
            <button class="btn btn-sm bg-gradient-dark text-white mb-0" @click="save">Save</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal: Sub Projects Detail -->
    <Teleport to="body">
      <div v-if="showDetailModal" class="modal-overlay">
        <div class="modal-box" style="width:760px;">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">{{ selectedProject.projectName }} — Sub Projects</h6>
          </div>
          <div class="p-3">
            <div v-if="activeSubs.length === 0" class="text-center py-4 text-secondary text-sm">
              No active sub projects.
            </div>
            <div v-else class="pm-table">
              <div class="pm-table-header">
                <span class="col-sub-name">SUB PROJECT</span>
                <span class="col-sub-date text-center">LAUNCH DATE</span>
                <span class="col-sub-date text-center">DUE DATE</span>
                <span class="col-sub-pm text-center">PM / PO</span>
                <span class="col-sub-status text-center">STATUS</span>
              </div>
              <div v-for="sub in activeSubs" :key="sub.subProjectId" class="pm-row">
                <div class="col-sub-name">
                  <p class="pm-item-name mb-0">{{ sub.subProjectName }}</p>
                </div>
                <div class="col-sub-date text-center">
                  <span class="text-sm text-secondary">{{ formatDate(sub.launchDate) }}</span>
                </div>
                <div class="col-sub-date text-center">
                  <span class="text-sm text-secondary">{{ formatDate(sub.dueDate) }}</span>
                </div>
                <div class="col-sub-pm text-center">
                  <span class="text-sm text-secondary">{{ sub.ownerFullName ? formatAssignedName(sub.ownerFullName) : '—' }}</span>
                </div>
                <div class="col-sub-status text-center">
                  <span class="status-pill" :class="statusPillClass(sub.subProjectStatus)">
                    {{ statusLabel(sub.subProjectStatus) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex justify-content-end p-3 border-top">
            <button class="btn btn-sm btn-outline-secondary mb-0" @click="showDetailModal = false">Close</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { projectsApi, subProjectsApi } from "@/services/backendApi";
import { statusLabel, statusPillClass } from "@/utils/subProjectStatus";

export default {
  name: "projects-table",
  data() {
    return {
      loading: false,
      showEditModal: false,
      showDetailModal: false,
      editId: null,
      saveError: null,
      selectedProject: {},
      activeSubs: [],
      form: { name: "" },
      projects: [],
    };
  },
  async created() {
    await this.loadProjects();
  },
  methods: {
    formatAssignedName(rawName) {
      const name = String(rawName || "").trim();
      if (!name) return "—";
      const nicknameMatch = name.match(/\(([^)]+)\)/);
      const firstName = name.replace(/\([^)]*\)/g, " ").trim().split(/\s+/)[0] || "";
      if (nicknameMatch && nicknameMatch[1]) {
        return `${firstName} (${nicknameMatch[1].trim()})`;
      }
      return firstName;
    },
    async loadProjects() {
      this.loading = true;
      try { this.projects = await projectsApi.getAll(); }
      catch (e) { console.error("loadProjects error:", e); }
      finally { this.loading = false; }
    },
    async openDetail(p) {
      this.selectedProject = p;
      this.activeSubs = [];
      this.showDetailModal = true;
      try { this.activeSubs = await subProjectsApi.getByProjectActive(p.projectId); }
      catch (e) { console.error("loadSubs error:", e); }
    },
    formatDate(val) {
      if (!val) return "—";
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split("-");
      return `${d}-${m}-${y}`;
    },
    statusLabel,
    statusPillClass,
    openCreate() {
      this.editId = null;
      this.form = { name: "" };
      this.showEditModal = true;
    },
    openEdit(p) {
      this.editId = p.projectId;
      this.form = { name: p.projectName };
      this.showEditModal = true;
    },
    closeEditModal() { this.showEditModal = false; this.saveError = null; },
    async save() {
      if (!this.form.name.trim()) return;
      this.saveError = null;
      try {
        if (this.editId) await projectsApi.update(this.editId, this.form.name.trim());
        else await projectsApi.insert(this.form.name.trim());
        await this.loadProjects();
        this.closeEditModal();
      } catch (e) {
        console.error("save project error:", e);
        this.saveError = e.message || "Save failed";
      }
    },
    async remove(p) {
      if (!confirm(`Delete "${p.projectName}"?`)) return;
      try {
        await projectsApi.delete(p.projectId);
        await this.loadProjects();
      } catch (e) {
        console.error("delete project error:", e);
        alert(e.message || "Delete failed");
      }
    },
  },
};
</script>

<style scoped>
.pm-table.pm-tab-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
}
.pm-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.pm-row {
  display: flex;
  align-items: center;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}
.pm-row:last-child { border-bottom: none; }
.pm-row:hover { background: #fafbfc; }

/* Project table cols */
.col-name   { flex: 5; min-width: 0; }
.col-total  { flex: 1.5; min-width: 80px; }
.col-action { flex: 0.8; min-width: 70px; }

/* Detail modal cols */
.col-sub-name   { flex: 3; min-width: 0; }
.col-sub-date   { flex: 1.2; min-width: 90px; }
.col-sub-pm     { flex: 1.5; min-width: 100px; }
.col-sub-status { flex: 1.2; min-width: 90px; }

.pm-name-btn {
  background: transparent;
  border: 0;
  padding: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
  cursor: pointer;
  text-align: left;
}
.pm-name-btn:hover { color: #5e72e4; }
.pm-item-name { font-size: 0.875rem; font-weight: 600; color: #344767; }

/* Action buttons */
.action-edit   { color: #4361ee; }
.action-edit:hover { color: #2d4bc8; }
.action-delete { color: #e74c3c; }
.action-delete:hover { color: #c0392b; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
}
</style>
